import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useDispatch } from "react-redux";
import { setSelectedHub } from "@/redux/supervisorDetailsSlice";
import { clearAuth } from "@/redux/authSlice";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { LogOutIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/themeConfigs/ThemeContext";
import { removeActiveFleetId } from "@/utils/appInitializer";

interface ProfileHoverProps {
  name?: string;
  hubList: { name: string }[];
  selectedHub: string;
  hubSelectionDisabled?: boolean;
  phone?: string,
}

export const ProfileHover: React.FC<ProfileHoverProps> = ({
  name,
  hubList,
  selectedHub,
  hubSelectionDisabled = false,
  phone = null
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {theme}= useTheme();

  const initials = name
    ? name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .toUpperCase()
    : "";

  const handleHubChange = (hubName: string) => {
    dispatch(setSelectedHub(hubName));
  };

  const handleLogout = () => {
    // Clear token from Redux store
    dispatch(clearAuth());
    // Remove token from localStorage
    localStorage.removeItem("auth_token");
    removeActiveFleetId();
    navigate("/signin");
  };

  return (
    <div className="flex flex-row items-center gap-[7px]">
      <Popover>
        <PopoverTrigger disabled={!theme.layout.backButton}>
          <Avatar className="cursor-pointer">
            <AvatarImage />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </PopoverTrigger>
        <PopoverContent className="w-36 p-2 flex flex-col items-center">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full flex items-center justify-center gap-1"
          >
            <LogOutIcon className="w-4 h-4" />
            Logout
          </Button>
        </PopoverContent>
      </Popover>

      <div className="flex flex-col gap-[8px]">
        <h1 className="leading-[16px] font-bold">{name || ""}</h1>
        <Select disabled={hubSelectionDisabled} onValueChange={handleHubChange} value={selectedHub}>
          <SelectTrigger className="w-full text-primary-foreground bg-transparent border outline-none focus:ring-0 focus:border-transparent">
            <SelectValue placeholder="Select a hub" />
          </SelectTrigger>
          <SelectContent className="w-full">
            <SelectGroup>
              <SelectLabel>Hubs</SelectLabel>
              {hubList.map((hub, index) => (
                <SelectItem
                  key={index}
                  value={hub.name}
                  className="focus:ring-0 focus:border-transparent active:border-transparent"
                >
                  {hub.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {
          phone &&
          <h1 className="leading-[16px] text-secondary">{phone || ""}</h1>
        }
      </div>
    </div>
  );
};
